import {
  type FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';

import isURL from 'validator/lib/isURL.js';

import {
  Button,
  Checkbox,
  CustomDropdown,
  Input,
  Link,
  TextArea,
  ThemeProvider,
} from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';
import VideoThumbnail from 'components/VideoThumbnail';
import {
  AssetPreview,
  type RefT,
} from 'components/admin/ArtistMediaManager/AssetPreview';
import {
  GalleryPhotos,
} from 'components/admin/ArtistMediaManager/GalleryPhotos';
import { Library } from 'components/admin/ArtistMediaManager/Library';
import {
  UsePhotoModal,
} from 'components/admin/ArtistMediaManager/UsePhotoModal';

import { useUserAssets } from 'hooks';
import { useGallery } from 'hooks/artists/useGallery';
import { useProfileHeroImage } from 'hooks/artists/useProfileHeroImage';

import type { UserUpdateT } from 'server/controllers/user';

import {
  saveGallery,
  setProfileHeroImage,
  uploadDocument,
} from 'services';

import { deleteAsset } from 'services/assets';

import {
  type ArtistSelfUpdateT,
  type ArtistT,
  type ImageAssetT,
  MODALITIES,
  type ModalityT,
  P_ADMIN_PROFILE_INTRO_VIDEO,
  type PublicUserDataT,
  type UserT,
  areEqualArraySets,
  getId,
  getVimeoVideoId,
} from 'utils';

import IconInfo from 'assets/images/icon-info-filled-20px.svg';

import IconVideo24 from 'assets/images/icon-video-24px.svg';

import secondaryButtonTheme from 'themes/Button/admin/secondary-v2.scss';
import dropdownTheme
  from 'themes/CustomDropdown/admin/with-inner-label-60px.scss';

import inputTheme from 'themes/Input/admin/with-inner-label-60px.scss';

import S from './style.scss';
import videoThumbnailTheme from './video-thumbnail.scss';
import assetPreviewTheme
  from '../ArtistMediaManager/ProfilePhoto/asset-preview.scss';

function isValidWebsite(website?: null | string): boolean {
  return !website || isURL(website);
}

type LocalGalleryItemT = File | ImageAssetT | null;

function equalGalleries(
  gallery: ImageAssetT[],
  local: LocalGalleryItemT[],
): boolean {
  for (let i = 0; i < Math.max(gallery.length, local.length); ++i) {
    const loc = local[i];

    if (loc) {
      if (loc instanceof File || loc.id !== gallery[i]?.id) return false;
    } else if (gallery[i]) return false;
  }

  return true;
}

function preventDefault(e: Event) {
  e.preventDefault();
}

/**
 * Mind that in case of practitioners their profile information is actually
 * spread across multiple database object, thus this complex structure of
 * the update object.
 */
export type ProfileUpdateT = {
  newAvatar?: File;
  practitioner?: ArtistSelfUpdateT;
  user?: UserUpdateT;
};

type PropsT = ({
  editable: true;
  updateUser: (update: ProfileUpdateT) => Promise<void> | void;
} | {
  editable?: false;
}) & {
  introVideoUrl?: string;
  practitioner?: ArtistT | null;
  user: Partial<UserT> & PublicUserDataT;
  withAvatarAndIntroUpload?: boolean;
};

// Note: At the moment there is not much benefit to break this component
// apart to satisfy the function complexity limit.
// eslint-disable-next-line complexity
const AdminUserProfileData: FunctionComponent<PropsT> = (props) => {
  type EditableProps = {
    updateUser: (update: ProfileUpdateT) => Promise<void> | void;
  };
  const {
    introVideoUrl,
    practitioner,
    user,
  } = props;

  useEffect(() => {
    window.addEventListener('dragover', preventDefault);
    window.addEventListener('drop', preventDefault);
    return () => {
      window.removeEventListener('dragover', preventDefault);
      window.removeEventListener('drop', preventDefault);
    };
  }, []);

  // const avatarInputRef = useRef<HTMLInputElement>(null);
  const assetPreviewRef = useRef<RefT>(null);

  // Load profile hero image
  const profileHeroImage = useProfileHeroImage(practitioner?.id ?? '');

  // Load gallery
  const gallery = useGallery(practitioner?.id ?? '');

  // Helper function to normalize gallery to fixed size
  const MAX_GALLERY_SIZE = 8;
  const normalizeGallery = (
    galleryData: ImageAssetT[] | null,
  ): Array<File | ImageAssetT | null> => {
    if (!galleryData) return new Array<null>(MAX_GALLERY_SIZE);
    if (galleryData.length >= MAX_GALLERY_SIZE) return galleryData;
    const res: Array<File | ImageAssetT | null> = galleryData.slice();
    while (res.length < MAX_GALLERY_SIZE) res.push(null);
    return res;
  };

  const [localProfilePhoto, setLocalProfilePhoto] = useState<
    File | ImageAssetT | null
  >(() => profileHeroImage.data ?? null);

  const [lastHeroData, setLastHeroData] = useState(profileHeroImage.data);
  if (profileHeroImage.data !== lastHeroData) {
    const wasInSync = localProfilePhoto === (lastHeroData ?? null);
    setLastHeroData(profileHeroImage.data);
    if (wasInSync) setLocalProfilePhoto(profileHeroImage.data ?? null);
  }

  const [profilePhotoFiles, setProfilePhotoFiles] = useState<File[]>([]);
  const [profilePhotoSaving, setProfilePhotoSaving] = useState(false);

  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [gallerySaving, setGallerySaving] = useState(false);

  const [localGallery, setLocalGallery] = useState<
    Array<File | ImageAssetT | null>
  >(() => normalizeGallery(gallery.data));

  const [lastGalleryData, setLastGalleryData] = useState(gallery.data);
  if (gallery.data !== lastGalleryData) {
    const wasInSync = equalGalleries(lastGalleryData ?? [], localGallery);
    setLastGalleryData(gallery.data);
    if (wasInSync) setLocalGallery(normalizeGallery(gallery.data));
  }

  // Loading states for save buttons
  const [introSaving, setIntroSaving] = useState(false);
  const [certificationsSaving, setCertificationsSaving] = useState(false);
  const [socialLinksSaving, setSocialLinksSaving] = useState(false);

  // Library state
  const [libraryLocalFiles, setLibraryLocalFiles] = useState<File[]>([]);
  const [usePhotoModal, setUsePhotoModal] = useState<File | ImageAssetT>();

  // Synchronization of state with props is handled by using a 'key' prop on
  // the component in the parent, which forces a re-mount when the practitioner
  // changes. Thus, these effects are largely redundant and can be removed
  // to avoid cascading renders.
  // However, some might still be needed if data updates WITHOUT a key change
  // (unlikely here as practitioner.id is used).
  // I will remove them and see if lint passes and behavior remains consistent.

  const [localDisplayName, setLocalDisplayName] = useState(
    practitioner?.displayName
    ?? (user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : ''),
  );
  const [localSummaryLine, setLocalSummaryLine] = useState(
    practitioner?.summaryLine ?? practitioner?.shortIntro ?? '',
  );
  const summaryLineWordCount = localSummaryLine
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  const maxSummaryLineWords = 50;

  const [localBio, setLocalBio] = useState(practitioner?.bio ?? '');
  const bioWordCount = localBio
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
  const maxBioWords = 100;

  const [localInstagram, setLocalInstagram] = useState(
    practitioner?.instagram,
  );
  const [localTikTok, setLocalTikTok] = useState(practitioner?.tikTok);
  const [localWebsite, setLocalWebsite] = useState(practitioner?.website);
  const [localYoutube, setLocalYoutube] = useState(practitioner?.youtube);
  const [localFacebook, setLocalFacebook] = useState(practitioner?.facebook);
  const [localLinkedIn, setLocalLinkedIn] = useState(practitioner?.linkedIn);
  const [localXTwitter, setLocalXTwitter] = useState(practitioner?.xTwitter);

  // Certifications and Speciality Areas state
  const [localCertifications, setLocalCertifications] = useState<string[]>(
    practitioner?.certificationsAndTraining ?? [],
  );
  const [localSpecialities, setLocalSpecialities] = useState<string[]>(
    practitioner?.specialityAreas ?? [],
  );
  const [certificationInput, setCertificationInput] = useState('');
  const [specialityInput, setSpecialityInput] = useState('');
  const [localCustomModalities, setLocalCustomModalities] = useState<string[]>(
    practitioner?.customModalities ?? [],
  );
  const [localModalities, setLocalModalities] = useState<ModalityT[]>(
    practitioner?.modalities ?? [],
  );
  const [customModality, setCustomModality] = useState('');

  // Social links visibility state
  const [showWebsiteOnPage, setShowWebsiteOnPage] = useState(
    practitioner?.showWebsiteOnPage ?? false,
  );
  const [showInstagramOnPage, setShowInstagramOnPage] = useState(
    practitioner?.showInstagramOnPage ?? false,
  );
  const [showTikTokOnPage, setShowTikTokOnPage] = useState(
    practitioner?.showTikTokOnPage ?? false,
  );
  const [showYoutubeOnPage, setShowYoutubeOnPage] = useState(
    practitioner?.showYoutubeOnPage ?? false,
  );
  const [showFacebookOnPage, setShowFacebookOnPage] = useState(
    practitioner?.showFacebookOnPage ?? false,
  );
  const [showLinkedInOnPage, setShowLinkedInOnPage] = useState(
    practitioner?.showLinkedInOnPage ?? false,
  );
  const [showXTwitterOnPage, setShowXTwitterOnPage] = useState(
    practitioner?.showXTwitterOnPage ?? false,
  );

  // Other links state
  const [localOtherLinks, setLocalOtherLinks] = useState<string[]>(
    practitioner?.otherLinks?.length ? practitioner.otherLinks : [''],
  );

  // Other links show on page state - initialize from saved data or defaults to false
  const [showOtherLinksOnPage, setShowOtherLinksOnPage] = useState<boolean[]>(
    () => {
      if (!practitioner?.otherLinks?.length) {
        return [false];
      }
      // Load saved visibility states or default to false for each link
      return practitioner.otherLinks.map(
        (_, index) => practitioner.showOtherLinksOnPage?.[index] ?? false,
      );
    },
  );

  // Count selected checkboxes for main social links (for disable logic)
  const selectedCheckboxesCount = [
    showWebsiteOnPage,
    showInstagramOnPage,
    showTikTokOnPage,
    showYoutubeOnPage,
    showFacebookOnPage,
    showLinkedInOnPage,
    showXTwitterOnPage,
  ].filter(Boolean).length;

  // Count total displayed across all social links and other links (max 6)
  const totalDisplayedCount = [
    showWebsiteOnPage,
    showInstagramOnPage,
    showTikTokOnPage,
    showYoutubeOnPage,
    showFacebookOnPage,
    showLinkedInOnPage,
    showXTwitterOnPage,
    ...showOtherLinksOnPage,
  ].filter(Boolean).length;
  const maxDisplayedLinks = 6;

  const isIntroSectionDirty = localDisplayName
    !== (practitioner?.displayName
      ?? (user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : ''))
      || localSummaryLine
      !== (practitioner?.summaryLine ?? (practitioner?.shortIntro ?? ''))
      || localBio !== (practitioner?.bio ?? '');

  const isCertificationsAndSpecialitiesDirty = JSON.stringify(
    localCertifications,
  )
  !== JSON.stringify(practitioner?.certificationsAndTraining ?? [])
  || JSON.stringify(localSpecialities)
  !== JSON.stringify(practitioner?.specialityAreas ?? []);

  const isModalitiesDirty = !areEqualArraySets(
    localCustomModalities,
    practitioner?.customModalities ?? [],
  )
  || !areEqualArraySets(localModalities, practitioner?.modalities ?? []);

  const isProfilePhotoDirty = profilePhotoFiles.length > 0
    || (localProfilePhoto instanceof File)
    || (localProfilePhoto
      && !(localProfilePhoto instanceof File)
      && localProfilePhoto.id !== profileHeroImage.data?.id);

  const isSocialLinksDirty = localFacebook !== (practitioner?.facebook ?? '')
    || localInstagram !== (practitioner?.instagram ?? '')
    || localLinkedIn !== (practitioner?.linkedIn ?? '')
    || localTikTok !== (practitioner?.tikTok ?? '')
    || localWebsite !== (practitioner?.website ?? '')
    || localXTwitter !== (practitioner?.xTwitter ?? '')
    || localYoutube !== (practitioner?.youtube ?? '')
    || showFacebookOnPage !== (practitioner?.showFacebookOnPage ?? false)
    || showInstagramOnPage !== (practitioner?.showInstagramOnPage ?? false)
    || showLinkedInOnPage !== (practitioner?.showLinkedInOnPage ?? false)
    || showTikTokOnPage !== (practitioner?.showTikTokOnPage ?? false)
    || showWebsiteOnPage !== (practitioner?.showWebsiteOnPage ?? false)
    || showXTwitterOnPage !== (practitioner?.showXTwitterOnPage ?? false)
    || showYoutubeOnPage !== (practitioner?.showYoutubeOnPage ?? false)
    || JSON.stringify(
      localOtherLinks.filter((link: string) => link.trim()),
    )
    !== JSON.stringify(
      (practitioner?.otherLinks ?? []).filter((link: string) => link.trim()),
    )
    || JSON.stringify(
      showOtherLinksOnPage.filter(
        (_: boolean, index: number) => {
          const links = practitioner?.otherLinks ?? [];
          return links[index]?.trim();
        },
      ),
    )
    !== JSON.stringify(
      (practitioner?.showOtherLinksOnPage ?? []).filter(
        (_: boolean, index: number) => {
          const links = practitioner?.otherLinks ?? [];
          return links[index]?.trim();
        },
      ),
    );

  const [badLocalWebsite, setBadLocalWebsite] = useState(false);

  const vimeoVideoIds: Record<string, string> = {};

  if (practitioner?.videos) {
    for (const file of practitioner.videos) {
      vimeoVideoIds[file] = getVimeoVideoId(file);
    }
  }

  const availableModalities: ModalityT[] = [];
  {
    const modalitySet = new Set(localModalities);
    for (const modality of MODALITIES) {
      if (!modalitySet.has(modality)) availableModalities.push(modality);
    }
  }

  const allModalityChips = [
    ...localModalities.map((item) => ({
      type: 'standard' as const,
      value: item,
    })),
    ...localCustomModalities.map((item) => ({
      type: 'custom' as const,
      value: item,
    })),
  ];

  const assets = useUserAssets(practitioner?.user.id ?? '');

  // Calculate number of free gallery slots
  let numFreeGallerySlots = 0;
  for (const item of localGallery) {
    if (!item) ++numFreeGallerySlots;
  }

  // Check if gallery is dirty (has changes)
  const isGalleryDirty = galleryFiles.length > 0
    || !equalGalleries(gallery.data ?? [], localGallery);

  if (practitioner) {
    // NOTE: Certifications and speciality areas are now displayed in the dedicated section above
  }

  const isSaving = introSaving
    || certificationsSaving
    || profilePhotoSaving
    || socialLinksSaving
    || gallerySaving;

  const THROBBER_HIDE_DELAY = 1000;
  const [showThrobber, setShowThrobber] = useState(isSaving);
  if (isSaving && !showThrobber) setShowThrobber(true);

  useEffect(() => {
    if (isSaving) return undefined;
    const id = setTimeout(() => {
      setShowThrobber(false);
    }, THROBBER_HIDE_DELAY);
    return () => {
      clearTimeout(id);
    };
  }, [isSaving]);

  // eslint-disable-next-line react/destructuring-assignment
  const noPractitionerRecord = props.editable && !practitioner;

  return (
    <ThemeProvider themes={{ Input: inputTheme }}>
      <div className={S.container}>
        {showThrobber ? (
          <div styleName="savingOverlay">
            <div styleName="savingOverlayInner">
              <Throbber />
            </div>
          </div>
        ) : null}

        {noPractitionerRecord ? (
          <div styleName="noPractitionerNotice">
            Name, bio, certifications, modalities, social links, and gallery
            are only available for practitioner accounts.
          </div>
        ) : null}

        <div styleName="introSection">
          <div styleName="introSectionHeader">
            <div styleName="introSectionTitle">
              <span styleName="badge">1</span>
              {' '}
              Name, summary line, and bio
            </div>
            <div styleName="tagsContainer">
              <div styleName="tag">
                summary: 1 line
              </div>
              <div styleName="tag">
                bio: up to 100 words
              </div>
            </div>
          </div>

          <div styleName="displayNameRow">
            <Input
              label="Display Name"
              onChange={(e) => {
                const { value } = e.target;
                setLocalDisplayName(value);
              }}
              placeholder={`${user.firstName} ${user.lastName}`}
              value={localDisplayName}
            />
          </div>

          <div styleName="summaryLineWrapper">
            <TextArea
              label="Summary Line"
              onChange={(e) => {
                const { value } = e.target;
                setLocalSummaryLine(value);
              }}
              placeholder="One line that sits under your name"
              styleName="summaryLineTextarea"
              value={localSummaryLine}
            />
            <div
              styleName={`wordCountDisplay ${summaryLineWordCount > maxSummaryLineWords ? 'exceeded' : ''}`}
            >
              {summaryLineWordCount}
              {' '}
              /
              {' '}
              {maxSummaryLineWords}
              {' '}
              words
            </div>
          </div>

          <div styleName="bioWrapper">
            <TextArea
              label="Bio"
              onChange={(e) => {
                const { value } = e.target;
                setLocalBio(value);
              }}
              placeholder="Up to 100 words."
              styleName="bioTextarea"
              value={localBio}
            />
            <div
              styleName={`lineCountDisplay ${bioWordCount > maxBioWords ? 'exceeded' : ''}`}
            >
              {bioWordCount}
              {' '}
              /
              {' '}
              {maxBioWords}
              {' '}
              words
            </div>
          </div>

          <div styleName="introSaveButton">
            <Button
              disabled={
                !isIntroSectionDirty || introSaving || noPractitionerRecord
              }
              onClick={() => {
                // eslint-disable-next-line react/destructuring-assignment
                if (props.editable) {
                  void (async () => {
                    try {
                      setIntroSaving(true);
                      const update: ProfileUpdateT = {};

                      if (practitioner) {
                        update.practitioner = {
                          bio: localBio,
                          displayName: localDisplayName,
                          summaryLine: localSummaryLine,
                        };
                      }

                      const updateFn = (props as EditableProps).updateUser;
                      await updateFn(update);
                    } finally {
                      setIntroSaving(false);
                    }
                  })();
                }
              }}
            >
              Save
            </Button>
          </div>
        </div>

        <div styleName="certificationsSection">
          <div styleName="certificationsHeader">
            <div styleName="certificationsTitle">
              <span styleName="badge">2</span>
              {' '}
              Certifications and speciality areas
            </div>
            <div styleName="certificationsTagsContainer">
              <div styleName="certificationTag">
                certs: up to 5
              </div>
              <div styleName="specialityTag">
                specialities: up to 10
              </div>
            </div>
          </div>

          <div styleName="certificationsContent">
            <div styleName="certificationsColumn">
              <div styleName="columnHeader">
                <span styleName="columnTitle">Certification / training</span>
                <span styleName="itemCount">
                  {localCertifications.length}
                  /5
                </span>
              </div>
              <div styleName="itemsList">
                {localCertifications.map((cert) => (
                  <div key={cert} styleName="listItem">
                    <span>{cert}</span>
                    <button
                      onClick={() => {
                        setLocalCertifications(
                          localCertifications.filter((c) => c !== cert),
                        );
                      }}
                      styleName="removeButton"
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              {localCertifications.length < 5 ? (
                <div styleName="addItemSection">
                  <Input
                    label="Add Certification"
                    onChange={(e) => {
                      const { value } = e.target;
                      setCertificationInput(value);
                    }}
                    placeholder=""
                    value={certificationInput}
                  />
                  <button
                    onClick={() => {
                      if (
                        certificationInput.trim()
                        && localCertifications.length < 5
                      ) {
                        setLocalCertifications([
                          ...localCertifications,
                          certificationInput.trim(),
                        ]);
                        setCertificationInput('');
                      }
                    }}
                    styleName="addLink"
                    type="button"
                  >
                    + add certification
                  </button>
                </div>
              ) : null}
            </div>

            <div styleName="specialitiesColumn">
              <div styleName="columnHeader">
                <span styleName="columnTitle">Specialities</span>
                <span styleName="itemCount">
                  {localSpecialities.length}
                  /10
                </span>
              </div>
              <div styleName="itemsList">
                {localSpecialities.map((speciality) => (
                  <div key={speciality} styleName="listItem">
                    <span>{speciality}</span>
                    <button
                      onClick={() => {
                        setLocalSpecialities(
                          localSpecialities.filter((s) => s !== speciality),
                        );
                      }}
                      styleName="removeButton"
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              {localSpecialities.length < 10 ? (
                <div styleName="addItemSection">
                  <Input
                    label="Add Speciality"
                    onChange={(e) => {
                      const { value } = e.target;
                      setSpecialityInput(value);
                    }}
                    placeholder=""
                    value={specialityInput}
                  />
                  <button
                    onClick={() => {
                      if (
                        specialityInput.trim()
                        && localSpecialities.length < 10
                      ) {
                        setLocalSpecialities([
                          ...localSpecialities,
                          specialityInput.trim(),
                        ]);
                        setSpecialityInput('');
                      }
                    }}
                    styleName="addLink"
                    type="button"
                  >
                    + add speciality
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <div styleName="certificationsSaveButton">
            <Button
              disabled={
                !isCertificationsAndSpecialitiesDirty
                || certificationsSaving
                || noPractitionerRecord
              }
              onClick={() => {
                void (async () => {
                  try {
                    setCertificationsSaving(true);
                    const update: ProfileUpdateT = {
                      practitioner: {
                        certificationsAndTraining: localCertifications,
                        specialityAreas: localSpecialities,
                      },
                    };
                    const updateFnCert = (props as EditableProps).updateUser;
                    await updateFnCert(update);
                  } finally {
                    setCertificationsSaving(false);
                  }
                })();
              }}
            >
              Save
            </Button>
          </div>
        </div>

        <div styleName="modalitiesSection">
          <div styleName="modalitiesHeader">
            <div styleName="modalitiesTitle">
              <span styleName="badge">3</span>
              {' '}
              Hero photo and modalities
            </div>
            <div styleName="modalitiesTag">
              page shows 6 chips, then +X
            </div>
          </div>

          <div styleName="modalitiesContainer">
            <div styleName="profilePhotoColumn">
              <AssetPreview
                image={localProfilePhoto}
                noDelete
                ref={assetPreviewRef}
                setImage={(image) => {
                  if (image instanceof File
                    && !profilePhotoFiles.includes(image)
                  ) {
                    setProfilePhotoFiles([...profilePhotoFiles, image]);
                  }
                  setLocalProfilePhoto(image);
                }}
                theme={assetPreviewTheme}
              />
              <div styleName="profilePhotoOps">
                <Button theme={secondaryButtonTheme} to="#your-library">
                  Choose from library
                </Button>
                <p>
                  or &zwnj;
                  <Link
                    onClick={() => {
                      assetPreviewRef.current?.openPicker();
                    }}
                    to=""
                  >
                    upload new
                  </Link>
                </p>
              </div>
            </div>

            <div styleName="modalitiesColumn">
              <div styleName="modalitiesControls">
                {availableModalities.length > 0 && (
                  <CustomDropdown
                    label="Modalities"
                    onChange={(value) => {
                      setLocalModalities([
                        ...localModalities,
                        value as ModalityT,
                      ]);
                    }}
                    options={availableModalities}
                    theme={dropdownTheme}
                    value="Select"
                  />
                )}
              </div>

              <div styleName="modalitiesChips">
                {allModalityChips.map((chip) => (
                  <div
                    key={`${chip.type}/${chip.value}`}
                    styleName="modalityChip"
                  >
                    <span>{chip.value}</span>
                    <button
                      onClick={() => {
                        if (chip.type === 'standard') {
                          setLocalModalities(
                            localModalities.filter(
                              (item) => item !== chip.value,
                            ),
                          );
                        } else {
                          setLocalCustomModalities(
                            localCustomModalities.filter(
                              (item) => item !== chip.value,
                            ),
                          );
                        }
                      }}
                      styleName="removeButton"
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <div styleName="otherModalitiesBlock">
                <Input
                  label="Other Modality"
                  onChange={(e) => {
                    const { value } = e.target;
                    setCustomModality(value);
                  }}
                  onKeyDown={(e) => {
                    if (customModality && (e.key === 'Enter' || e.key === 'Tab')) {
                      if (!localCustomModalities.includes(customModality)) {
                        setLocalCustomModalities([
                          ...localCustomModalities,
                          customModality,
                        ]);
                      }
                      setCustomModality('');
                    }
                  }}
                  value={customModality}
                />
              </div>

              <div styleName="modalitiesSaveButton">
                <Button
                  disabled={(!isModalitiesDirty && !isProfilePhotoDirty)
                    || profilePhotoSaving
                    || noPractitionerRecord}
                  onClick={() => {
                    // eslint-disable-next-line react/destructuring-assignment
                    if (props.editable) {
                      void (async () => {
                        try {
                          setProfilePhotoSaving(true);

                          // Save profile photo if dirty
                          if (isProfilePhotoDirty && practitioner) {
                            const artistId = getId(practitioner);
                            const fileMap = new Map<File, string>();

                            // Upload new files
                            for (const file of profilePhotoFiles) {
                              const asset = await uploadDocument({
                                document: file,
                                practitionerId: artistId,
                              });
                              fileMap.set(file, asset.id);
                            }

                            // Save the hero image
                            if (localProfilePhoto instanceof File) {
                              const id = fileMap.get(localProfilePhoto);
                              if (!id) throw Error('Internal error');
                              const result = await setProfileHeroImage(
                                artistId,
                                id,
                              );
                              profileHeroImage.set(result);
                            } else if (
                              localProfilePhoto
                              && localProfilePhoto.id
                              !== profileHeroImage.data?.id
                            ) {
                              const result = await setProfileHeroImage(
                                artistId,
                                localProfilePhoto.id,
                              );
                              profileHeroImage.set(result);
                            }

                            setProfilePhotoFiles([]);
                          }

                          // Save
                          // modalities
                          const update = (
                            props as EditableProps
                          ).updateUser;
                          await update({
                            practitioner: {
                              customModalities: localCustomModalities,
                              modalities: localModalities,
                            },
                          });
                        } finally {
                          setProfilePhotoSaving(false);
                        }
                      })();
                    }
                  }}
                >
                  Save
                </Button>
              </div>

            </div>
          </div>
        </div>

        {/* Section 5: Social Links and Website */}
        <div styleName="socialLinksSection">
          <div styleName="socialLinksHeader">
            <div styleName="socialLinksTitle">
              <span styleName="badge">4</span>
              {' '}
              Social links and website
            </div>
            <div styleName="certificationsTagsContainer">
              <div styleName="certificationTag">
                save any number
              </div>
              <div styleName="specialityTag">
                display up to 6
              </div>
            </div>
          </div>

          <div styleName="socialLinksContent">
            {/* Website */}
            <div styleName="socialLinkRow">
              <Input
                label="Website"
                onChange={(e) => {
                  const { value } = e.target;
                  setLocalWebsite(value);
                  if (badLocalWebsite && isValidWebsite(value)) {
                    setBadLocalWebsite(false);
                  }
                }}
                value={localWebsite ?? ''}
              />
              <Checkbox
                checked={showWebsiteOnPage}
                disabled={selectedCheckboxesCount >= 5
                  && !showWebsiteOnPage}
                label="show on page"
                onChange={() => {
                  setShowWebsiteOnPage(!showWebsiteOnPage);
                }}
                styleName="showOnPageCheckbox"
              />
            </div>

            {/* Instagram */}
            <div styleName="socialLinkRow">
              <Input
                label="Instagram"
                onChange={(e) => {
                  let { value } = e.target;
                  if (value && !value.startsWith('@')) value = `@${value}`;
                  setLocalInstagram(value);
                }}
                placeholder="@handle"
                value={localInstagram ?? ''}
              />
              <Checkbox
                checked={showInstagramOnPage}
                disabled={selectedCheckboxesCount >= 5
                  && !showInstagramOnPage}
                label="show on page"
                onChange={() => {
                  setShowInstagramOnPage(!showInstagramOnPage);
                }}
                styleName="showOnPageCheckbox"
              />
            </div>

            {/* TikTok */}
            <div styleName="socialLinkRow">
              <Input
                label="TikTok"
                onChange={(e) => {
                  let { value } = e.target;
                  if (value && !value.startsWith('@')) value = `@${value}`;
                  setLocalTikTok(value);
                }}
                placeholder="@handle"
                value={localTikTok ?? ''}
              />
              <Checkbox
                checked={showTikTokOnPage}
                disabled={selectedCheckboxesCount >= 5
                  && !showTikTokOnPage}
                label="show on page"
                onChange={() => {
                  setShowTikTokOnPage(!showTikTokOnPage);
                }}
                styleName="showOnPageCheckbox"
              />
            </div>

            {/* YouTube */}
            <div styleName="socialLinkRow">
              <Input
                label="YouTube"
                onChange={(e) => {
                  let { value } = e.target;
                  if (value && !value.startsWith('@')) value = `@${value}`;
                  setLocalYoutube(value);
                }}
                placeholder="@handle"
                value={localYoutube ?? ''}
              />
              <Checkbox
                checked={showYoutubeOnPage}
                disabled={selectedCheckboxesCount >= 5
                  && !showYoutubeOnPage}
                label="show on page"
                onChange={() => {
                  setShowYoutubeOnPage(!showYoutubeOnPage);
                }}
                styleName="showOnPageCheckbox"
              />
            </div>

            {/* Facebook */}
            <div styleName="socialLinkRow">
              <Input
                label="Facebook"
                onChange={(e) => {
                  let { value } = e.target;
                  if (value && !value.startsWith('@')) value = `@${value}`;
                  setLocalFacebook(value);
                }}
                placeholder="@handle"
                value={localFacebook ?? ''}
              />
              <Checkbox
                checked={showFacebookOnPage}
                disabled={selectedCheckboxesCount >= 5
                  && !showFacebookOnPage}
                label="show on page"
                onChange={() => {
                  setShowFacebookOnPage(!showFacebookOnPage);
                }}
                styleName="showOnPageCheckbox"
              />
            </div>

            {/* LinkedIn */}
            <div styleName="socialLinkRow">
              <Input
                label="LinkedIn"
                onChange={(e) => {
                  let { value } = e.target;
                  if (value && !value.startsWith('@')) value = `@${value}`;
                  setLocalLinkedIn(value);
                }}
                placeholder="@handle"
                value={localLinkedIn ?? ''}
              />
              <Checkbox
                checked={showLinkedInOnPage}
                disabled={selectedCheckboxesCount >= 5
                  && !showLinkedInOnPage}
                label="show on page"
                onChange={() => {
                  setShowLinkedInOnPage(!showLinkedInOnPage);
                }}
                styleName="showOnPageCheckbox"
              />
            </div>

            {/* X/Twitter */}
            <div styleName="socialLinkRow">
              <Input
                label="X (Twitter)"
                onChange={(e) => {
                  let { value } = e.target;
                  if (value && !value.startsWith('@')) value = `@${value}`;
                  setLocalXTwitter(value);
                }}
                placeholder="@handle"
                value={localXTwitter ?? ''}
              />
              <Checkbox
                checked={showXTwitterOnPage}
                disabled={selectedCheckboxesCount >= 5
                  && !showXTwitterOnPage}
                label="show on page"
                onChange={() => {
                  setShowXTwitterOnPage(!showXTwitterOnPage);
                }}
                styleName="showOnPageCheckbox"
              />
            </div>

            {localOtherLinks.map((link, index) => (
              <div
                key={link || `other-link/${index}`}
                styleName="socialLinkRow"
              >
                <Input
                  label={`Other Link #${index + 1}`}
                  onChange={(e) => {
                    const { value } = e.target;
                    const links = [...localOtherLinks];
                    links[index] = value;
                    setLocalOtherLinks(links);
                  }}
                  value={link}
                />
                <Checkbox
                  checked={showOtherLinksOnPage[index] ?? false}
                  disabled={
                    totalDisplayedCount >= maxDisplayedLinks
                    && !showOtherLinksOnPage[index]
                  }
                  label="show on page"
                  onChange={() => {
                    const updated = [...showOtherLinksOnPage];
                    updated[index] = !updated[index];
                    setShowOtherLinksOnPage(updated);
                  }}
                  styleName="showOnPageCheckbox"
                />
              </div>
            ))}
            <div styleName="addAnotherLinkRow">
              <Link
                onClick={() => {
                  setLocalOtherLinks([...localOtherLinks, '']);
                  setShowOtherLinksOnPage([...showOtherLinksOnPage, false]);
                }}
                styleName="addAnotherLinkButton"
                to=""
              >
                + add another link
              </Link>
              <div styleName="otherLinksDisplayTag">
                {totalDisplayedCount}
                {' '}
                of
                {' '}
                {maxDisplayedLinks}
                {' '}
                displayed
              </div>
            </div>
          </div>

          <div styleName="socialLinksSaveButton">
            <Button
              disabled={
                !isSocialLinksDirty || socialLinksSaving || noPractitionerRecord
              }
              onClick={() => {
                // eslint-disable-next-line react/destructuring-assignment
                if (props.editable) {
                  void (async () => {
                    try {
                      setSocialLinksSaving(true);
                      const updateFn3 = (props as EditableProps).updateUser;
                      await updateFn3({
                        practitioner: {
                          facebook: localFacebook,
                          instagram: localInstagram,
                          linkedIn: localLinkedIn,
                          otherLinks: localOtherLinks.filter(
                            (link) => link.trim(),
                          ),
                          showFacebookOnPage,
                          showInstagramOnPage,
                          showLinkedInOnPage,
                          showOtherLinksOnPage: showOtherLinksOnPage.filter(
                            (_, index) => localOtherLinks[index]?.trim(),
                          ),
                          showTikTokOnPage,
                          showWebsiteOnPage,
                          showXTwitterOnPage,
                          showYoutubeOnPage,
                          tikTok: localTikTok,
                          website: localWebsite,
                          xTwitter: localXTwitter,
                          youtube: localYoutube,
                        },
                      });
                    } finally {
                      setSocialLinksSaving(false);
                    }
                  })();
                }
              }}
            >
              Save
            </Button>
          </div>
        </div>

        {/* Section 6: Intro Video and Gallery */}
        <div styleName="introVideoGallerySection">
          <div styleName="introVideoGalleryHeader">
            <div styleName="introVideoGalleryTitle">
              <span styleName="badge">5</span>
              {' '}
              Intro video and gallery
            </div>
            <div styleName="introVideoGalleryTag">
              gallery: up to 8
            </div>
          </div>

          <div styleName="introVideoGalleryContainer">
            <div styleName="introVideoColumn">
              {practitioner?.videoThumbnailUrl ? (
                <Link
                  styleName="introVideoThumbnail"
                  to={introVideoUrl ?? P_ADMIN_PROFILE_INTRO_VIDEO}
                >
                  <VideoThumbnail
                    imageUrl={practitioner.videoThumbnailUrl}
                    theme={videoThumbnailTheme}
                  />
                  <div styleName="editVideoLabel">
                    <IconVideo24 />
                    {' '}
                    Edit Intro
                  </div>
                </Link>
              ) : (
                <Link
                  styleName="uploadIntoVideoReminder"
                  to={introVideoUrl ?? P_ADMIN_PROFILE_INTRO_VIDEO}
                >
                  <div styleName="reminder">
                    <IconInfo />
                    {' '}
                    Reminder
                  </div>
                  <div styleName="message">
                    <IconVideo24 />
                    {' '}
                    Upload your intro video
                  </div>
                </Link>
              )}
            </div>

            <div styleName="galleryColumn">
              <GalleryPhotos
                images={localGallery}
                setImages={(images) => {
                  const localSet = new Set(galleryFiles);
                  for (const item of images) {
                    if (item instanceof File && !localSet.has(item)) {
                      setGalleryFiles((prev) => [...prev, item]);
                      localSet.add(item);
                    }
                  }
                  setLocalGallery(images);
                }}
              />
            </div>
          </div>

          <div styleName="introVideoGallerySaveButton">
            <Button
              disabled={
                !isGalleryDirty || gallerySaving || noPractitionerRecord
              }
              onClick={() => {
                // eslint-disable-next-line react/destructuring-assignment
                if (props.editable) {
                  void (async () => {
                    try {
                      setGallerySaving(true);

                      if (practitioner) {
                        const artistId = getId(practitioner);
                        const fileMap = new Map<File, string>();

                        // Upload new gallery files
                        for (const file of galleryFiles) {
                          const asset = await uploadDocument({
                            document: file,
                            practitionerId: artistId,
                          });
                          fileMap.set(file, asset.id);
                        }

                        // Map local gallery to IDs, uploading files as needed
                        const galleryIds: string[] = [];
                        for (const item of localGallery) {
                          if (item) {
                            if (item instanceof File) {
                              const id = fileMap.get(item);
                              if (id) galleryIds.push(id);
                            } else {
                              galleryIds.push(item.id);
                            }
                          }
                        }

                        // Save gallery to database
                        await saveGallery(artistId, galleryIds);
                        gallery.set(
                          galleryIds.map((id) => ({ id } as ImageAssetT)),
                        );
                        setGalleryFiles([]);

                        // Trigger updateUser to refresh the view
                        const updateFn4 = (props as EditableProps).updateUser;
                        await updateFn4({});
                      }
                    } finally {
                      setGallerySaving(false);
                    }
                  })();
                }
              }}
            >
              Save
            </Button>
          </div>
        </div>

        {/* Section 7: Your Library */}
        <div id="your-library" styleName="mediaLibrarySection">
          <div styleName="mediaLibraryHeader">
            <div styleName="mediaLibraryTitle">
              Your library
            </div>
            <div styleName="mediaLibraryTags">
              <div styleName="mediaTag">
                {(assets.data?.length ?? 0) + libraryLocalFiles.length}
                {' '}
                items
              </div>
              <div styleName="mediaTag">
                no upload limit
              </div>
            </div>
          </div>

          <Library
            assets={assets.data ?? []}
            localFiles={libraryLocalFiles}
            onUse={(item) => {
              if (!(item instanceof File) && item.kind !== 'IMAGE') {
                throw Error('Unexpected item kind');
              }
              setUsePhotoModal(item);
            }}
            removeAsset={(asset) => {
              void (async () => {
                // Remove from gallery if it's being used there
                let newGallery: typeof localGallery | undefined;
                for (let i = 0; i < localGallery.length; ++i) {
                  const item = localGallery[i];
                  if (item && !(item instanceof File) && item.id === asset.id) {
                    newGallery ??= localGallery.slice();
                    newGallery[i] = null;
                  }
                }
                if (newGallery) setLocalGallery(newGallery);

                // Remove from profile photo if it's being used there
                if (
                  localProfilePhoto
                  && !(localProfilePhoto instanceof File)
                  && localProfilePhoto.id === asset.id
                ) {
                  setLocalProfilePhoto(null);
                }

                // Delete the asset from server
                try {
                  await deleteAsset(asset.id);
                  // Reload assets list
                  await assets.reload();
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error('Failed to delete asset:', error);
                  // eslint-disable-next-line no-alert
                  alert('Failed to delete the asset. Please try again.');
                }
              })();
            }}
            setLocalFiles={(files) => {
              const fileSet = new Set(files);
              let newGallery: typeof localGallery | undefined;
              for (let i = 0; i < localGallery.length; ++i) {
                const item = localGallery[i];
                if (item instanceof File && !fileSet.has(item)) {
                  newGallery ??= localGallery.slice();
                  newGallery[i] = null;
                }
              }
              if (newGallery) setLocalGallery(newGallery);
              setLibraryLocalFiles(files);
            }}
          />
        </div>

        {usePhotoModal ? (
          <UsePhotoModal
            addAsHero={() => {
              setLocalProfilePhoto(usePhotoModal);
              if (usePhotoModal instanceof File
                && !profilePhotoFiles.includes(usePhotoModal)
              ) {
                setProfilePhotoFiles([...profilePhotoFiles, usePhotoModal]);
              }
              setUsePhotoModal(undefined);
            }}
            addToGallery={() => {
              let neu: typeof localGallery | undefined;
              for (let i = 0; i < localGallery.length; ++i) {
                if (!localGallery[i]) {
                  neu = localGallery.slice();
                  neu[i] = usePhotoModal;
                  break;
                }
              }
              if (!neu) throw Error('Internal error');
              if (usePhotoModal instanceof File
                && !galleryFiles.includes(usePhotoModal)
              ) {
                setGalleryFiles([...galleryFiles, usePhotoModal]);
              }
              setLocalGallery(neu);
              setUsePhotoModal(undefined);
            }}
            numFreeGallerySlots={numFreeGallerySlots}
            numGallerySlots={localGallery.length}
            onClose={() => {
              setUsePhotoModal(undefined);
            }}
            photo={usePhotoModal}
          />
        ) : null}
      </div>
    </ThemeProvider>
  );
};

export default AdminUserProfileData;
